/**
 * 웹 표면 — **선언 하나에서 전부 파생한다.** (docs/decisions/0008)
 *
 * ─── 왜 확장 슬롯이 `screens` 하나인가 ───
 * 계획서 §4 는 `routes`·`nav`·`i18n`·`widgets`·`quickActions` 다섯 개를 제안했다.
 * 구 stationon-erp 의 실측이 그 다섯을 하나로 줄이게 했다 — **같은 사실이 5곳에 손으로
 * 적혀 있었고 이미 어긋나 있었다**: `menuPermissions` · 라우트 meta · `pageTitleKeyMap`(33행) ·
 * `RolesView.vue`(896줄) · `bottomNavDefaultPaths`(17행). 역할 편집 화면은 어느 트리의
 * 자식 12개 중 7개만 나열했고, 주석 처리된 메뉴 2개를 계속 표시했다.
 * 내비·빵부스러기·문서제목·역할편집표는 전부 **파생**이다. 손으로 적을 자리가 없으면
 * 드리프트가 없다. (`module.ts:23-26` — 확장점은 독립된 vertical 둘이 같은 것을 필요로 할 때만)
 *
 * ─── 이 파일이 만드는 불변식 ───
 * 1. **경로는 `/<moduleKey>/<path>` 다. 예외 0개.** 모듈 키의 유일성은 `register()` 가
 *    보장하므로 **모듈 간 경로 충돌이 표현 불가능**하다. 화면 키도 `<moduleKey>.` 로
 *    시작하므로 같은 이유로 충돌이 불가능하다.
 * 2. **라우트 트리는 평평하다.** 여기에는 부모 화면이 없다 — `parentScreenKey` 도
 *    중첩 배열도 없다. React Router data 모드는 매칭된 브랜치의 **모든** loader 를 돌리므로
 *    중첩은 **선언되지 않은 permission 논리곱**을 만든다. 그 버그를 고치는 대신 그것이
 *    살던 구조를 없앴다(0008 "route-first 탈락").
 * 3. **내비에 뜨는 화면은 파라미터를 가질 수 없다.** `:runId` 를 채울 값이 내비에는 없다 —
 *    구 시스템의 **죽은 링크 6개**가 정확히 그 형태였다. 아래 `checkSurface` 가 막는다.
 * 4. **`requires` 는 permission 키 하나다. 배열이 아니다.** 논리곱이 필요하면 permission 을
 *    새로 선언한다 — 화면 구조가 권한 규칙을 발명하지 못하게 한다.
 *
 * ─── 이 파일이 하는 검사는 인가가 아니다 ───
 * `resolveSurface()` 가 granted 만 싣는 것은 **일관성**이지 보안 경계가 아니다.
 * 진짜 판정은 `permissionMiddleware` 가 `withTenant` 안에서 한다(rpc-kernel).
 * 화면이 열려도 그 안의 버튼이 부르는 procedure 는 서버가 다시 판정한다.
 * **여기서 '권한은 끝났다'고 생각하는 것이 다음 사고의 씨앗이다**(0008 §감수한 것).
 *
 * ─── 브랜드 타입을 만들지 않은 이유 (0008 §구현 순서 6 이 7번으로 넘긴 결정) ───
 * `enabledModules`·`granted` 를 `PermissionSet` 처럼 봉인하려면 생성자가 `packages/db`
 * 안에만 있어야 하는데 소비자(`resolveSurface`)는 여기 `packages/contracts` 에 산다.
 * 타입을 여기 두는 순간 누구나 만들 수 있어 브랜드가 **일관성 표시로만** 남는다 —
 * 그건 이름값을 못 하는 브랜드다. 그래서 둘 다 `ReadonlySet<string>` 이고
 * (`ExtensionRegistry.get()` 이 이미 쓰는 형태다), 대신 **가장 아픈 사고 하나를 단언으로
 * 막는다**: 플랫폼 모듈이 활성 목록에서 빠지면 `resolveSurface` 가 던진다(락아웃 2층).
 *
 * ─── 지문은 여기, 다이제스트는 서버 (§구현 순서 8, 2026-09-02) ───
 * `surfaceHash` 를 한 함수로 두지 못한 이유는 하나다: contracts 는 **node 내장 import 0건**
 * 이어야 브라우저의 `packages/rpc-client` 가 이 패키지를 쓸 수 있다(0008 blocking ①).
 * 그래서 **무엇이 표면 변경인가**(= 정본 직렬화 `surfaceFingerprint`)만 여기 두고,
 * sha256 은 `apps/server` 가 찍는다. 손으로 해시 함수를 짜서 여기 넣는 선택지도 있었지만
 * 그건 검증된 다이제스트를 자작으로 바꾸는 것이고, 쪼갠 쪽은 **어느 필드가 스큐를
 * 만드는가**가 이 파일에 남는다는 이점이 있다.
 *
 * ─── 아직 여기 없는 것 (0008 이 미룬 것들) ───
 * - **native 표면**(G7 의 "native ⊆ web"): 모바일 앱이 0개라 지금 필드를 만들면 검사 대상이
 *   0개인 게이트가 된다 — `audit.ts` 가 "대상 0개는 통과가 아니라 무력화"라고 막은 형태다.
 * - **경로 → 화면 역방향 매칭**(`describePath`): §구현 순서 11에서 그 procedure 와 같이 정한다.
 */
import { PERMISSION_KEY_RE, type PermissionKeyShape, type RbacManifest } from './rbac.js';
import { checkI18nKeys, type I18nKeyUse } from './i18n.js';

/**
 * 화면 경로의 한 조각. 소문자 kebab — permission 키와 같은 어휘다.
 * **빈 문자열을 통과시키지 않는다**: `path: ''`(모듈 루트 화면)를 허용하면
 * `absolutePathOf` 가 `/core/`(후행 슬래시)를 내고 빵부스러기의 조상 판정이 조용히 갈린다.
 * 로그인 직후 착지는 그 대신 **내비 첫 항목**이다(0008 blocking ③).
 */
export const SCREEN_PATH_SEGMENT_PATTERN = '^[a-z][a-z0-9]*(-[a-z0-9]+)*$';
export const SCREEN_PATH_SEGMENT_RE = new RegExp(SCREEN_PATH_SEGMENT_PATTERN);

/** 경로 파라미터. `:runId` — camelCase 를 허용한다(코드에서 그대로 프로퍼티가 된다). */
export const SCREEN_PATH_PARAM_RE = /^:[a-z][A-Za-z0-9]*$/;

/** 화면 키. `<moduleKey>.<rest>` — 2조각 이상. */
export const SCREEN_KEY_PATTERN = '^[a-z][a-z0-9]*(-[a-z0-9]+)*(\\.[a-z][a-z0-9]*(-[a-z0-9]+)*)+$';
export const SCREEN_KEY_RE = new RegExp(SCREEN_KEY_PATTERN);

/**
 * 화면의 두 종류.
 *
 * 커널의 문 셋(`procedure({permission})`·`authenticatedProcedure`·`publicProcedure`)과
 * **일부러 다르다** — 화면에는 `authenticated` 가 없다. 그런 화면(권한과 무관하게 로그인만
 * 하면 보이는 것)이 아직 0개인데 먼저 만들면 "permission 을 붙이기 싫을 때의 탈출구"가
 * 되고, 웹에는 커널의 `rbac:check` 같은 계수기도 없다.
 * **재검토 트리거**: 로그인한 모두에게 보여야 하는 화면(내 정보 등)이 생기는 첫 PR.
 */
/**
 * 화면의 종류 — **커널의 문과 같은 수다.**
 *
 * `procedure({permission})` · `authenticatedProcedure` · `publicProcedure` 셋에 대응한다
 * (0008 의 표면과 0007·0009 의 커널이 같은 축을 갖는다는 뜻이다).
 * `authenticated` 는 2026-09-04 에 왔다 — **알림함**이 그 첫 화면이다:
 * 자기 앞으로 온 알림은 **권한 0개인 신규 직원도 봐야 하고**, 그걸 permission 으로 걸면
 * 역할이 없는 사람에게는 "승인됐습니다"가 영원히 안 보인다.
 */
export type ScreenKind = 'permission' | 'public' | 'authenticated';

/**
 * 내비 구획 — **2단 내비의 1단이다. 모듈이 아니라 '하는 일'로 나눈다.** (0057)
 *
 * ─── 왜 모듈 축을 버렸나 (2026-09-12) ───
 * 아래 `deriveNav` 에 2026-09-02 부터 재검토 트리거가 적혀 있었다: *"한 모듈의 비-hidden
 * 화면이 7개를 넘는 첫 PR"*. 그 사이에 `core` 가 **15개** · `time` 11 · `pay` 8 이 됐다.
 * 넘친 것이 길이만이었으면 순서만 고치면 됐겠지만, 실제로 드러난 것은 **축이 틀렸다**는
 * 것이다 — 권한 0개 신규 직원이 보는 화면 넷(알림·할일·내 근태·내 급여)이
 * **모듈 머리글 넷에 하나씩** 흩어져 있었고, 그 머리글은 '코어'·'근태·휴가'처럼
 * 그 사람에게 아무 뜻도 없는 **구독 단위의 이름**이다.
 *
 * 모듈 축은 **라이선스의 축**이다(`tenant_enabled_modules`). 사람이 일을 찾는 축과
 * 같아야 할 이유가 처음부터 없었고, 화면이 다섯 개일 때는 둘이 우연히 같아 보였을 뿐이다.
 *
 * ─── 구 시스템에서 가져온 것은 축 하나다 ───
 * 구 `menu.ts`(487줄)의 1단은 `내 것 / 인사(hr) / 재무(fin) / 회계(acc) / 관리(admin)` 였다.
 * **가져온 것은 "부서가 아니라 일로 나눈다"는 축뿐이고**, 그 목록을 그대로 베끼지 않았다:
 * 구 재무/회계 분리는 화면이 각각 2·3개인 데다 같은 급여를 두 곳에서 다르게 부르고 있었다.
 * 아이콘 46개 · 3단 트리 · `menuPermissions` 손 관리 목록은 **가져오지 않는다** —
 * 그 셋이 0008 이 탈락시킨 형태 그 자체다.
 *
 * ─── 이 목록이 네 번째 손 관리 목록이 아닌 이유 ───
 * 아래 `deriveNav` 가 아이콘을 거절한 근거는 "닫힌 목록이 하나 더 는다"였다. 구획은
 * 다르다: **화면마다 반드시 하나 골라야 하는 필수 필드**라 빠뜨림이 불가능하고
 * (`checkSurface` 가 문자열을 대조한다), 목록에 없는 구획은 타입이 거절한다.
 *
 * ★ **아이콘은 2026-09-16 에 들어왔고(0118), 그 근거는 뒤집히지 않았다** — 배치가
 * 바뀌었다. `icon` 은 여기서 **필수 필드**가 되어 빠뜨림이 불가능해졌고(구획과 같은 성질),
 * 이름의 **목록은 코어가 안 든다**(`packages/ui` 의 `NAV_ICONS`). 즉 "닫힌 목록이 코어에
 * 하나 더 는다"도 "안 붙여도 서니까 조용히 어긋난다"도 성립하지 않는다.
 *
 * **순서가 곧 내비 순서이고, 첫 구획의 첫 화면이 착지다**(`landingOf`).
 */
export const NAV_SECTIONS = [
  /**
   * 내 것 — **권한 0개인 사람에게 보이는 유일한 구획이고, 그래서 첫째다.**
   *
   * 대시보드가 여기 있는 것이 이 구획의 정의다: 그 화면이 답하는 질문은
   * "오늘 **내가** 알아야 하는 것"이고(0028), 착지 화면이므로 첫 항목이어야 한다.
   * 구 리포는 대시보드를 구획 밖 단독 항목으로 뒀는데, 그러면 머리글 없는 항목이라는
   * **두 번째 모양**이 생기고 구획 접기에서 그 하나만 규칙이 달라진다.
   */
  { key: 'my', labelKey: 'core.nav.my', icon: 'person' },
  /** 사람 — 명부·조직·그 사람에 붙는 서류. "이 사람이 누구인가"에 답하는 화면들. */
  { key: 'people', labelKey: 'core.nav.people', icon: 'people' },
  /** 근태·휴가 — 날짜에 붙는 것. 매일 도는 주기다. */
  { key: 'attendance', labelKey: 'core.nav.attendance', icon: 'calendar-check' },
  /** 급여·정산 — 돈 계산. 매월·분기로 도는 주기다. */
  { key: 'payroll', labelKey: 'core.nav.payroll', icon: 'wallet' },
  /**
   * 설정 — **한 번 정하고 거의 안 여는 것.** 권한·구독·회사·정책·서명 채널.
   *
   * 마지막인 이유가 그것이다. 구획 안의 화면들이 서로 다른 모듈 소유인 것도 여기가
   * 가장 심한데(코어·time·pay·esign 넷), **그게 이 축의 요점이다**: 휴가 종류를
   * 정하는 일과 역할을 정하는 일은 같은 사람이 같은 날 한다.
   */
  { key: 'settings', labelKey: 'core.nav.settings', icon: 'settings' },
] as const;

export type NavSectionKey = (typeof NAV_SECTIONS)[number]['key'];

/**
 * 구획을 코어가 소유한다는 말의 타입 판 — **vertical 은 코어 구획에 들어갈 수 없다.**
 *
 * 0008 §감수한 것이 "vertical 은 자기 내비 위치를 정할 수 없다"였고 그 결정은 유효하다.
 * vertical 이 `'payroll'` 을 고를 수 있으면 그 순간 코어 구획의 내용이 **모듈 목록에
 * 따라 달라지고**, 코어가 vertical 을 모른다는 1번 규칙이 내비에서 뒤집힌다.
 * 그래서 vertical 의 화면은 `'own-module'` 하나만 쓴다 — 자기 모듈 라벨이 곧 구획이 되고,
 * 그 구획은 코어 구획들 **뒤에** 모듈 등록 순서로 선다.
 *
 * ★ 모듈 **안**에서는 구획을 나눌 수 있다(0099, `ModuleNavGroup`). 그것이 이 규칙을
 *   뒤집지 않는 이유는 구획 키가 `<moduleKey>.<groupKey>` 라서다 — 코어 구획 이름 공간에
 *   닿지 않고, 자리도 그대로 코어 구획 전부의 뒤다.
 *
 * 물음표가 없는 이유는 `surface` 와 같다: 선택 필드는 빠뜨림이 기본값이 된다.
 * 판정은 `checkSurface`(문자열 대조)와 `registry.register()`(vertical 쪽)가 나눠 한다.
 */
export const OWN_MODULE_SECTION = 'own-module';
export type ScreenSection = NavSectionKey | typeof OWN_MODULE_SECTION;

interface ScreenDefBase {
  /** `<moduleKey>.<rest>`. `ScreenLink` 가 경로 대신 이걸 쓴다 — vertical 코드에 경로 문자열이 등장하지 않는다. */
  readonly key: string;
  /** 모듈 안에서의 경로. 절대경로는 `absolutePathOf()` 가 만든다 — 여기에 `/` 를 적지 않는다. */
  readonly path: string;
  /** 내비 라벨. `<moduleKey>.screen.<rest>` */
  readonly labelKey: string;
  /**
   * 이 화면이 사는 **내비 구획**. 물음표가 없다 — 빠뜨리면 컴파일이 죽는다.
   *
   * hidden 화면도 적는다. 내비에는 안 뜨지만 **빵부스러기의 1단이 이 값**이라,
   * 안 적으면 상세 화면에서만 위치 표시가 다른 말을 하게 된다.
   */
  readonly section: ScreenSection;
  /** 문서 제목. 없으면 `labelKey` 를 쓴다 — 구 시스템의 `pageTitleKeyMap`(33행)이 이 기본값 하나로 사라진다. */
  readonly titleKey?: string;
  /** 내비에 안 뜨는 화면(상세·편집). 라우트는 있다. 파라미터가 있는 화면은 반드시 이쪽이다. */
  readonly hidden?: boolean;
  /**
   * 모듈 **안**에서의 정렬. 같으면 선언 순서(정렬은 stable 하다).
   * 모듈 **간** 정렬은 코어가 소유한다 — vertical 은 자기 내비 위치를 정할 수 없다.
   * (0008 §감수한 것: "이 설계의 가장 약한 관절. 세 번째 vertical 이 왔을 때 압력이 온다")
   */
  readonly order?: number;
  /**
   * 이 화면이 사는 **모듈 안의 구획**(0099). `section: 'own-module'` 일 때만 쓴다.
   *
   * 물음표가 있는 이유는 `titleKey` 와 같다 — **대부분의 모듈에 없는 것이 정상**이고,
   * 있는 모듈에서는 빠뜨림이 불가능하다: `navGroups` 를 선언한 모듈의 own-module 화면에
   * 이 칸이 없으면 `checkSurface` 가 거절한다(그 판정이 아래 §구획 블록에 있다).
   * 반대로 `navGroups` 가 없는 모듈이나 코어 구획 화면에 이 칸이 있으면 그것도 거절이다 —
   * 아무도 안 보는 값이 선언에 남아 있으면 다음 사람이 그것이 먹힌다고 믿는다.
   */
  readonly group?: string;
  /**
   * 구획 **안의 하위 구획**(0118). 자기 `group` 이 `subgroups` 를 선언했을 때만 쓴다.
   *
   * `group` 과 같은 성질이다 — 선언한 구획에서는 빠뜨림이 불가능하고, 안 선언한 구획에
   * 적으면 거절이다. 판정 넷이 `checkSurface` 에 있다.
   */
  readonly subgroup?: string;
  /**
   * 내비 아이콘 이름. **물음표가 없다**(0118).
   *
   * 선택 필드로 두면 `surface` 가 `surface?` 였을 때와 같은 일이 벌어진다 — 안 붙여도
   * 화면이 서므로 빠뜨림이 기본값이 되고, 목록의 절반만 아이콘을 가진 내비가 된다.
   * 그 상태는 아이콘이 없는 것보다 나쁘다: 없는 것은 규칙이고 절반은 고장이다.
   *
   * 값의 **목록은 여기 없다** — `packages/ui` 의 `NAV_ICONS` 가 소유하고 `pnpm web:check`
   * 가 둘을 양방향으로 잇는다. 코어가 목록을 들면 `camera` 같은 vertical 어휘가
   * 코어 도메인으로 들어오고, 그게 이 필드를 오래 거절했던 이유다.
   */
  readonly icon: string;
}

export interface PermissionScreenDef extends ScreenDefBase {
  readonly kind: 'permission';
  /**
   * 이 화면을 여는 데 필요한 permission 키 **하나**.
   *
   * 타입은 4세그먼트를 강제하지 못한다 — `${string}` 이 점을 포함하므로
   * `'a.b.c.d.e'` 도 컴파일된다(2026-09-01 tsc 5.9.3 실측). 판정은 `checkSurface` 가 한다.
   * **검증 없는 불변식은 불변식이 아니라 희망이다**(0005 §55-57).
   */
  readonly requires: PermissionKeyShape;
}

export interface PublicScreenDef extends ScreenDefBase {
  readonly kind: 'public';
  /** 미인증 화면에는 permission 이 없다. 타입으로 막고 `checkSurface` 가 런타임으로 한 번 더. */
  readonly requires?: never;
}

/**
 * 로그인하면 **누구나** 여는 화면. permission 이 없다.
 *
 * `public` 과 다른 점: 미인증이 아니다(세션이 필요하다). 그래서 내비에 뜨고,
 * 착지 후보이며, 매니페스트에 항상 실린다(모듈이 켜져 있는 한).
 * 쓸 자리의 기준은 커널의 세 번째 문과 **같다**(AGENTS.md 14):
 * *부르는 사람 자신에 대한 것이고, 권한 0개 상태에서도 답이 있어야 하는 것.*
 * 그 밖은 전부 `kind: 'permission'` 이다 — 여기가 넓어지면 표면 감사가 볼 것이 줄어든다.
 */
export interface AuthenticatedScreenDef extends ScreenDefBase {
  readonly kind: 'authenticated';
  /** 권한을 안 본다. 타입으로 막고 `checkSurface` 가 런타임으로 한 번 더. */
  readonly requires?: never;
}

export type ScreenDef = PermissionScreenDef | PublicScreenDef | AuthenticatedScreenDef;

/**
 * 모듈의 웹 표면. **`surface?` 의 물음표가 없는 것이 핵심이다.**
 *
 * 구 시스템은 라우트 115개 중 **20개**에 권한 meta 가 없었다 — 선택 필드는 빠뜨림이
 * 기본값이 된다. 화면이 없는 모듈은 `{ headless: '<이유>' }` 로만 그 사실을 말할 수 있다.
 * `publicProcedure({ reason })` 과 같은 수법이다: 없다는 것도 **적어야** 한다.
 */
export type ModuleSurface =
  | {
      readonly screens: readonly ScreenDef[];
      readonly navGroups?: readonly ModuleNavGroup[];
      /**
       * `navGroups` 가 **없는** 모듈의 1단 아이콘(0118). 그때는 모듈 자체가 구획이라
       * 아이콘의 출처가 여기밖에 없다(코어 구획은 `NAV_SECTIONS`, 나눈 모듈은 `navGroups[].icon`).
       *
       * 물음표가 없다 — 있으면 "구획을 안 나눈 모듈만 아이콘이 없는 내비"가 생긴다.
       * 구획을 나눈 모듈에서는 이 값이 안 읽히고, 그 상태를 `checkSurface` 가 거절한다.
       */
      readonly navIcon: string;
      readonly headless?: never;
    }
  | {
      readonly headless: string;
      readonly screens?: never;
      readonly navGroups?: never;
      readonly navIcon?: never;
    };

/**
 * **모듈 안의 내비 구획**(2026-09-15, 0099). vertical 하나가 구획 여럿으로 선다.
 *
 * ─── 왜 생겼나 ───
 * `deriveNav` 가 자기 주석에 재검토 트리거를 미리 적어 뒀다: *"한 구획의 화면이 12개를
 * 넘는 첫 PR. … 그 위로는 3단이 아니라 **구획을 쪼개는 것**이 답이다."* `stationon` 이
 * **23개**로 그 선을 두 배 넘겼고, 넘친 것이 길이만이 아니었다 — 한 머리글 아래
 * *창고에서 원단을 자르는 일* 과 *쿠팡에 상품을 올리는 일* 과 *원단 마스터를 고치는 일* 이
 * 섞여 있었다. 0057 이 코어에서 발견한 것("모듈 축은 라이선스의 축이고 사람이 일을 찾는
 * 축과 같아야 할 이유가 없다")이 vertical 안에서 한 번 더 나타난 것이다.
 *
 * ─── 규칙 66 을 뒤집지 않는다 ───
 * 66 이 막는 것은 **vertical 이 코어 구획을 고르는 것**이다. 그 근거는 "코어 구획의 내용이
 * 모듈 목록에 따라 달라진다"였고, 여기서는 그 일이 일어나지 않는다: 구획 키가
 * `<moduleKey>.<groupKey>` 라 코어 구획 이름 공간에 닿지 않고, 자리도 그대로
 * **코어 구획 전부의 뒤**다. 달라지는 것은 모듈 하나가 차지하는 머리글 개수뿐이다.
 *
 * ─── 3단이 생겼다 (2026-09-16, 0118) ───
 * 위 문단은 *"3단이면 접힘 상태가 두 층이 되고 `openSections` 하나로는 표현할 수 없다"* 로
 * 3단을 거절했다. **그 반론이 틀렸다** — 하위 구획 키를 `<sectionKey>/<subgroupKey>` 로
 * 전역 유일하게 만들면 문자열 배열 **하나**가 두 층을 그대로 표현한다. 두 모양이 아니다.
 *
 * 그리고 0099 §8 이 예고한 날이 왔다: *"RG 출고는 분할이 끝나면 14개가 되어 다시 넘는다.
 * 그때는 쪼갤 축이 없다(구 ERP 가 14개를 한 메뉴에 뒀고 그게 지시다). … 답이 '쪼갠다'가
 * 아닐 수 있다는 것을 미리 적어 둔다."* 소유자가 그 답을 3단으로 정했다(전역 §28).
 *
 * 66 이 지키던 것은 그대로다: 1단은 코어가 소유하고, vertical 은 `'own-module'` 하나만
 * 쓰며, 늘어난 것은 **모듈 안의 깊이**뿐이다 — 0099 가 `navGroups` 에 대해 쓴 논증이
 * 한 층 더 적용된 것이고 코어 이름 공간에는 여전히 안 닿는다.
 */
export interface ModuleNavGroup {
  /** 모듈 안에서 유일. 구획 키는 `<moduleKey>.<key>` 로 조립된다 — 여기에 모듈 키를 다시 적지 않는다. */
  readonly key: string;
  /** 구획 이름. `<moduleKey>.nav.<rest>` — 코어 구획과 같은 kind 다(둘 다 선언된 닫힌 목록이다). */
  readonly labelKey: string;
  /** 구획 아이콘. 화면의 `icon` 과 같은 이름 공간이다(`packages/ui` 의 `NAV_ICONS`). */
  readonly icon: string;
  /**
   * 이 구획의 **하위 구획**(0118). 없으면 구획이 화면을 직접 든다(2단).
   *
   * **둘 이상이거나 없다.** 하나짜리는 "안 나눈다"와 "나누려다 말았다"가 같은 모양이
   * 되는 자리이고, 0099 가 `navGroups` 에서 이미 거절한 형태다.
   */
  readonly subgroups?: readonly ModuleNavSubgroup[];
}

/**
 * 구획 안의 한 묶음. 아이콘이 **없다** — 세 층이 전부 아이콘을 달면 내비가 웅성거리고,
 * BI §10 "Calm density" 가 막으려는 것이 정확히 그 소리다. 2단은 들여쓰기와 활자로
 * 구분되고, 그 둘이면 충분하다는 것이 이 자리의 판단이다.
 */
export interface ModuleNavSubgroup {
  /** 그 구획 안에서 유일. 내비 키는 `<sectionKey>/<key>` 로 조립된다. */
  readonly key: string;
  /** `<moduleKey>.nav.<rest>` */
  readonly labelKey: string;
}

/** 표면을 가진 모듈 — `CatalogModuleInput` 에 `surface` 를 더한 구조적 부분집합이다. */
export interface SurfaceModule {
  readonly key: string;
  /** 내비 1단 라벨. `<moduleKey>.module.label` */
  readonly labelKey: string;
  /** false = 플랫폼 모듈. 끌 수 없다 — 아래 락아웃 단언이 이것으로 판정한다. */
  readonly licensable: boolean;
  readonly surface: ModuleSurface;
}

/** 조립된 표면의 한 줄. 모듈 경계를 넘어 평평하다 — 트리가 아니다. */
export interface SurfaceCatalogEntry {
  readonly moduleKey: string;
  readonly moduleLabelKey: string;
  /** 모듈이 곧 구획일 때의 1단 아이콘(0118). 구획을 나눈 모듈에서는 안 읽힌다. */
  readonly moduleIcon: string;
  /** 해소된 모듈 안 구획(0099). 선언에 없으면 `null` — 그 모듈은 구획 하나짜리다. */
  readonly group: ManifestScreenGroup | null;
  /** 해소된 하위 구획(0118). 구획이 `null` 이면 이것도 `null` 이다. */
  readonly subgroup: ManifestScreenSubgroup | null;
  readonly screen: ScreenDef;
  /** `/<moduleKey>/<path>`. 유일한 생산자는 `absolutePathOf()` 다 — 문자열을 다시 조립하지 않는다. */
  readonly path: string;
}

/** 매니페스트가 싣는 모듈 안 구획. `ModuleNavGroup` 에 **선언 순서**를 해소해 더한 것이다. */
export interface ManifestScreenGroup {
  readonly key: string;
  readonly labelKey: string;
  /** 모듈 안에서의 구획 순서 = `navGroups` 선언 순서. 모듈 **간** 순서는 여전히 코어가 소유한다. */
  readonly order: number;
  readonly icon: string;
}

/**
 * 매니페스트가 싣는 하위 구획(0118). 화면마다 실리는 이유는 `group` 과 같다 —
 * 배열을 쪼개면 둘이 어긋난 상태가 표현 가능해진다.
 */
export interface ManifestScreenSubgroup {
  readonly key: string;
  readonly labelKey: string;
  /** 구획 안에서의 순서 = `subgroups` 선언 순서. */
  readonly order: number;
}

/**
 * 매니페스트의 한 줄. **선택 필드가 없다** — 기본값 해소(`titleKey`·`hidden`·`order`)가
 * 여기서 한 번 끝나므로 소비자가 그 기본값을 다시 구현할 자리가 없다.
 */
export interface ManifestScreen {
  readonly key: string;
  readonly path: string;
  readonly moduleKey: string;
  /**
   * 모듈 라벨을 화면마다 들고 있는 것은 정규화가 아니다 — 의도한 것이다.
   * 매니페스트를 `{screens, modules}` 두 배열로 쪼개면 **둘이 어긋난 상태**가 표현 가능해진다.
   * 배열 하나에서 파생하면 어긋남이 불가능하다.
   */
  readonly moduleLabelKey: string;
  /**
   * 모듈이 곧 구획일 때의 1단 아이콘(0118). `moduleLabelKey` 를 화면마다 들고 있는 것과
   * 같은 판단이다 — 배열을 쪼개면 둘이 어긋난 상태가 표현 가능해진다.
   */
  readonly moduleIcon: string;
  /**
   * 구획. `'own-module'` 은 여기까지 그대로 온다 — **해소하지 않는다.**
   * 해소해서 `moduleKey` 를 넣으면 "코어 구획 'core'"와 "자기 모듈이 구획인 vertical"이
   * 같은 모양이 되고, 그러면 구획 순서를 정할 때 둘을 다시 구분할 방법이 없다.
   */
  readonly section: ScreenSection;
  /**
   * 모듈 안의 구획(0099). **없으면 `null` 이다 — 선택 필드가 아니다.**
   * `section` 과 같은 이유로 해소하지 않는다: 소비자가 `?? undefined` 로 기본값을
   * 다시 구현할 자리를 안 만든다.
   *
   * 키만이 아니라 **이름과 순서까지** 여기 실린다. `moduleLabelKey` 를 화면마다 들고
   * 있는 것과 같은 판단이다(위 주석): 매니페스트를 `{screens, groups}` 두 배열로 쪼개면
   * **둘이 어긋난 상태가 표현 가능해지고**, 화면 하나에서 파생하면 어긋남이 불가능하다.
   * 특히 `order` 가 없으면 구획 순서가 "그 구획의 첫 화면이 몇 번째로 선언됐나"로 정해진다 —
   * `deriveNav` 가 코어 구획에서 이미 거절한 형태다(`sectionIndex` 선점이 그 거절이다).
   */
  readonly group: ManifestScreenGroup | null;
  /**
   * 구획 안의 하위 구획(0118). `group` 과 같은 이유로 **없으면 `null`** 이다.
   * `group` 이 `null` 이면 이것도 반드시 `null` 이다 — `checkSurface` 가 그 짝을 본다.
   */
  readonly subgroup: ManifestScreenSubgroup | null;
  readonly labelKey: string;
  readonly titleKey: string;
  readonly kind: ScreenKind;
  readonly hidden: boolean;
  readonly order: number;
  readonly icon: string;
}

/** 서버가 계산해 보내는 것. **granted 만 들어 있다** — 클라이언트는 필터하지 않는다. */
export interface SurfaceManifest {
  readonly screens: readonly ManifestScreen[];
}

/**
 * 내비의 한 구획. **키가 구획 키이거나 모듈 키다** — 그 둘이 한 이름 공간에 섞이지
 * 않는 이유는 `NAV_SECTIONS` 의 키가 예약어이기 때문이 아니라, vertical 쪽 키가
 * `RESERVED_MODULE_KEYS` 를 못 쓰기 때문이다(`registry.ts`).
 */
export interface NavSection {
  readonly key: string;
  readonly labelKey: string;
  readonly icon: string;
  /**
   * 구획 **직속** 화면. 하위 구획이 있는 구획에서는 빈 배열이다.
   *
   * ★ **`screens` 와 `groups` 는 동시에 차지 않는다**(`checkSurface` 가 막는다).
   * 섞이면 "머리글 없는 항목"이라는 두 번째 모양이 생기고, 그건 0057 이 대시보드를
   * 구획 밖 단독 항목으로 두지 않기로 하면서 이미 거절한 형태다.
   */
  readonly screens: readonly ManifestScreen[];
  /** 하위 구획(0118). 없으면 빈 배열 — 그 구획은 2단이다. */
  readonly groups: readonly NavSubgroup[];
}

/** 내비 3단의 가운데. 키는 `<sectionKey>/<subgroupKey>` 라 **전역 유일**이다. */
export interface NavSubgroup {
  readonly key: string;
  readonly labelKey: string;
  readonly screens: readonly ManifestScreen[];
}

export interface Breadcrumb {
  readonly labelKey: string;
  /** 모듈 조각에는 경로가 없다 — 모듈 인덱스 화면을 만들지 않았기 때문이다. */
  readonly path?: string;
}

export interface SurfaceProblem {
  readonly where: string;
  readonly reason: string;
}

export class SurfaceError extends Error {
  constructor(readonly problems: readonly SurfaceProblem[]) {
    super(
      `표면 선언 검사 실패 (${problems.length}건):\n` +
        problems.map((p) => `  ✗ ${p.where}\n      ${p.reason}`).join('\n'),
    );
    this.name = 'SurfaceError';
  }
}

/**
 * 락아웃 — 플랫폼 모듈이 활성 목록에 없다.
 *
 * 이게 일어나면 `/core/*` 전체가 매니페스트에서 사라지고 **구독을 되살릴 화면도 못 연다.**
 * 빈 매니페스트로 조용히 진행하지 않는 이유: 그 상태는 "화면이 하나도 없는 신규 직원"과
 * 구분되지 않고, UI 에서 "권한이 없습니다"라는 **거짓말**이 된다.
 * 이건 락아웃 방어 3층 중 2층이다 — 1층 `enabledModulesOf()`(뷰 vs 카탈로그 표),
 * 3층 마이그레이션 0010 의 `do $$` 구조 단언.
 */
export class SurfaceLockoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SurfaceLockoutError';
  }
}

/** `/<moduleKey>/<path>`. 경로를 만드는 **유일한 자리**다. */
export function absolutePathOf(moduleKey: string, path: string): string {
  return `/${moduleKey}/${path}`;
}

/**
 * 선언된 경로 템플릿에 파라미터를 채운다. **링크를 만드는 유일한 자리다.**
 *
 * 여기 있는 이유는 `absolutePathOf` 와 같다: 경로의 문법(`:runId` 가 파라미터라는 사실)을
 * 아는 곳이 하나여야 한다. web-sdk 가 자기 자리에서 `replace(/:\w+/)` 를 적으면
 * 그 정규식과 `SCREEN_PATH_PARAM_RE` 가 갈라질 수 있고, 갈라진 날의 증상은
 * **주소창에 `:runId` 가 그대로 남은 404** 다.
 *
 * 빠진 파라미터에 던지는 이유: 안 던지면 그 404 가 조용히 나간다. 링크 하나가 안 그려지는 것이
 * 화면 전체가 죽는 것보다 낫다고 볼 수도 있지만, 그건 **선언과 코드가 어긋난 상태**이고
 * 이 리포는 그것을 시끄럽게 만드는 쪽을 반복해서 골라 왔다(0008 §enforcement-first).
 */
export function fillScreenPath(path: string, params: Readonly<Record<string, string>> = {}): string {
  return path
    .split('/')
    .map((seg) => {
      if (!seg.startsWith(':')) return seg;
      const name = seg.slice(1);
      const v = params[name];
      if (v === undefined || v === '') {
        throw new SurfaceError([{
          where: `fillScreenPath('${path}')`,
          reason: `경로 파라미터 '${name}' 에 줄 값이 없다. 그대로 두면 주소창에 ':${name}' 이 남고 404 가 된다.`,
        }]);
      }
      // 값에 슬래시가 들어오면 세그먼트가 하나 늘어 **다른 화면의 라우트**에 매칭될 수 있다.
      return encodeURIComponent(v);
    })
    .join('/');
}

/** 파라미터 이름을 지운 경로 모양. `runs/:id` 와 `runs/:runId` 는 같은 라우트다. */
function pathShapeOf(path: string): string {
  return path
    .split('/')
    .map((s) => (s.startsWith(':') ? ':' : s))
    .join('/');
}

/** 선언된 화면들. headless 모듈은 0줄. */
export function screensOf(surface: ModuleSurface): readonly ScreenDef[] {
  return surface.screens ?? [];
}

/**
 * 모듈 목록 → 평평한 표면 카탈로그. 모듈 순서 = 입력 순서(= 코어가 소유하는 내비 순서).
 * **검증은 하지 않는다** — `assertSurface()` 가 `register()` 시점에 이미 했다고 가정한다.
 */
export function toSurfaceCatalog(modules: readonly SurfaceModule[]): SurfaceCatalogEntry[] {
  const out: SurfaceCatalogEntry[] = [];
  for (const m of modules) {
    /*
     * 구획 선언 순서를 **여기서 한 번** 해소한다(0099). 아래에서 `find` 로 찾지 않는 이유는
     * 순서가 필요하기 때문이다 — 선언 순서가 곧 내비 구획 순서이고, 그 숫자를 만드는 자리는
     * 하나여야 한다. 선언에 없는 group 키는 `undefined` 로 떨어지는데, 그 상태는
     * `checkSurface` 가 이미 거절했다(검증 없는 불변식은 희망이다 — 0005 §55-57).
     */
    const groups = new Map<string, ManifestScreenGroup>(
      (m.surface.navGroups ?? []).map((g, i) => [
        g.key, { key: g.key, labelKey: g.labelKey, order: i, icon: g.icon },
      ]),
    );
    /* 하위 구획도 같은 자리에서 한 번 해소한다(0118) — 키는 `<group>/<subgroup>` 이다. */
    const subgroups = new Map<string, ManifestScreenSubgroup>(
      (m.surface.navGroups ?? []).flatMap((g) =>
        (g.subgroups ?? []).map((sg, i): [string, ManifestScreenSubgroup] => [
          `${g.key}/${sg.key}`, { key: sg.key, labelKey: sg.labelKey, order: i },
        ])),
    );
    for (const screen of screensOf(m.surface)) {
      const group = screen.group === undefined ? null : groups.get(screen.group) ?? null;
      out.push({
        moduleKey: m.key,
        moduleLabelKey: m.labelKey,
        moduleIcon: m.surface.navIcon ?? '',
        group,
        subgroup: screen.subgroup === undefined || group === null
          ? null
          : subgroups.get(`${group.key}/${screen.subgroup}`) ?? null,
        screen,
        path: absolutePathOf(m.key, screen.path),
      });
    }
  }
  return out;
}

/** 선언에 올 수 있는 구획 값 전부. 검사가 문자열을 대조하는 자리다. */
const SECTION_VALUES = new Set<string>([...NAV_SECTIONS.map((s) => s.key), OWN_MODULE_SECTION]);

/** 실패 메시지의 자리. 한 형식만 쓴다 — 검사마다 다른 모양이면 문제 목록을 훑을 수 없다. */
function whereOf(moduleKey: string, screenKey: string, field: string): string {
  return `${moduleKey}.screens[${screenKey}].${field}`;
}

/**
 * 화면이 쓰는 i18n 키. `i18n.ts` 의 검사기가 이 목록을 받는다 —
 * **규약을 여기에 다시 적지 않는다**(`<moduleKey>.<kind>.<rest>` 판정기는 하나뿐이다).
 */
/**
 * 구획 이름의 i18n 키. **모듈 순회에 안 잡힌다** — 구획은 어느 모듈의 것도 아니라서,
 * 이 줄이 없으면 구획 하나를 더하고 문장을 안 써도 게이트가 통과하고 내비 머리글에
 * `core.nav.…` 가 그대로 뜬다(0009 의 "키를 그대로 그린다"가 그때 나타난다).
 */
export function navSectionI18nKeys(): I18nKeyUse[] {
  return NAV_SECTIONS.map((s) => ({
    key: s.labelKey,
    // 주인은 `core` 다 — 구획 목록이 코어 계약이기 때문이다(`collectErrorI18nKeys` 와 같은 자리).
    owner: 'core',
    kind: 'nav' as const,
    where: `NAV_SECTIONS[${s.key}].labelKey`,
  }));
}

export function collectSurfaceI18nKeys(mod: { key: string; surface: ModuleSurface }): I18nKeyUse[] {
  const out: I18nKeyUse[] = [];
  /*
   * 모듈 안의 구획 이름(0099). `navSectionI18nKeys` 가 코어 구획에 대해 하는 일의
   * vertical 판이고, 빠지면 증상도 같다 — 내비 머리글에 `stationon.nav.…` 가 그대로 뜬다.
   */
  for (const g of mod.surface.navGroups ?? []) {
    out.push({ key: g.labelKey, owner: mod.key, kind: 'nav', where: `${mod.key}.navGroups[${g.key}].labelKey` });
    /* 하위 구획 이름(0118). 빠지면 증상이 구획과 같다 — 머리글에 키가 그대로 뜬다. */
    for (const sg of g.subgroups ?? []) {
      out.push({
        key: sg.labelKey, owner: mod.key, kind: 'nav',
        where: `${mod.key}.navGroups[${g.key}].subgroups[${sg.key}].labelKey`,
      });
    }
  }
  for (const s of screensOf(mod.surface)) {
    out.push({ key: s.labelKey, owner: mod.key, kind: 'screen', where: whereOf(mod.key, s.key, 'labelKey') });
    if (s.titleKey !== undefined) {
      out.push({ key: s.titleKey, owner: mod.key, kind: 'screen', where: whereOf(mod.key, s.key, 'titleKey') });
    }
  }
  return out;
}

/**
 * 모듈 하나의 표면 선언 검사. **던지지 않는다** — 호출자가 전부 모아 한 번에 보고할 수 있게.
 *
 * 다른 모듈의 permission 키를 참조하는 것은 여기서 판정하지 않는다(카탈로그가 조립된 뒤
 * `web:check` 가 본다). `registry.ts` 의 roleTemplate 검사와 같은 이유다 —
 * **등록 순서에 의존하는 검사를 여기서 하면 모듈 순서가 곧 배선이 된다.**
 */
export function checkSurface(mod: {
  key: string;
  rbac: RbacManifest;
  surface: ModuleSurface;
}): SurfaceProblem[] {
  const problems: SurfaceProblem[] = [];
  const at = (what: string) => `${mod.key}.surface${what}`;
  const push = (where: string, reason: string) => problems.push({ where, reason });

  /**
   * `surface` 는 타입상 필수다. 그래도 여기서 보는 이유는 `requires` 를
   * `typeof !== 'string'` 으로 보는 것과 같다 — 캐스트 한 번이나 JS 소비자 하나면
   * 이 자리에 `undefined` 가 온다. 그때 나는 것이 TypeError 면
   * **어느 모듈이 범인인지 안 알려 준다.**
   */
  if (typeof mod.surface !== 'object' || mod.surface === null) {
    push(at(''), `표면 선언 자체가 없다. 화면이 없는 모듈도 { headless: '<이유>' } 로 적는다.`);
    return problems;
  }

  const hasScreens = mod.surface.screens !== undefined;
  const hasHeadless = mod.surface.headless !== undefined;

  if (hasScreens && hasHeadless) {
    push(at(''), `screens 와 headless 를 동시에 선언했다. 화면이 있으면 screens 만, 없으면 headless 만 쓴다.`);
    return problems;
  }
  if (!hasScreens && !hasHeadless) {
    push(at(''), `screens 도 headless 도 없다. 화면이 없다는 것도 선언해야 한다 — { headless: '<이유>' }`);
    return problems;
  }
  if (hasHeadless) {
    if (mod.surface.headless!.trim().length < 4) {
      push(
        at('.headless'),
        `이유가 비었다. 'publicProcedure({ reason })' 과 같은 규칙이다 — 화면이 없는 이유를 적으면 ` +
          `다음 사람이 "아직 안 만든 것"과 "만들지 않기로 한 것"을 구분할 수 있다.`,
      );
    }
    return problems;
  }

  const screens = mod.surface.screens!;
  if (screens.length === 0) {
    push(
      at('.screens'),
      `화면이 0개다. 빈 배열 대신 { headless: '<이유>' } 로 적는다 — 빈 배열은 "없다"와 "빠뜨렸다"가 같은 모양이다.`,
    );
    return problems;
  }

  const declaredPermissions = new Set(mod.rbac.permissions.map((p) => p.key as string));
  const seenKeys = new Set<string>();
  const seenShapes = new Map<string, string>();

  /* ── 1단 아이콘(0118) ── */
  if (typeof mod.surface.navIcon !== 'string' || mod.surface.navIcon.length === 0) {
    push(
      at('.navIcon'),
      `모듈의 내비 아이콘이 없다. 구획을 나눈 모듈에서는 navGroups[].icon 이 이기지만, ` +
        `그 모듈이 구획을 접는 날 이 값이 1단 아이콘이 된다 — 그때 빠져 있으면 조용히 빈칸이다.`,
    );
  }

  /* ── 모듈 안의 구획(0099) ── */
  const navGroups = mod.surface.navGroups;
  const groupKeys = new Set<string>();
  /** 구획 키 → 그 구획이 선언한 하위 구획 키들. 하위 구획이 없는 구획은 여기 없다(0118). */
  const subgroupKeys = new Map<string, Set<string>>();
  if (navGroups !== undefined) {
    if (navGroups.length === 0) {
      push(
        at('.navGroups'),
        `구획을 빈 배열로 선언했다. 구획이 하나뿐인 모듈은 이 필드를 **쓰지 않는다** — ` +
          `빈 배열은 "구획을 안 나눈다"와 "나누려다 말았다"가 같은 모양이다.`,
      );
    }
    if (navGroups.length === 1) {
      push(
        at('.navGroups'),
        `구획이 하나뿐이다. 그 상태는 필드를 생략한 것과 내비가 **똑같이 그려지는데** ` +
          `이름만 하나 더 생긴다(모듈 라벨 대신 구획 라벨). 나눌 것이 생길 때 더한다.`,
      );
    }
    for (const g of navGroups) {
      const where = `${mod.key}.surface.navGroups[${g.key}]`;
      if (!SCREEN_PATH_SEGMENT_RE.test(g.key)) {
        push(`${where}.key`, `구획 키 '${g.key}' 가 소문자 kebab(${SCREEN_PATH_SEGMENT_PATTERN}) 이 아니다. ` +
          `이 값은 내비 구획 키 '<moduleKey>.<key>' 의 뒷자리이고 브라우저 저장소에도 그대로 남는다.`);
      }
      if (groupKeys.has(g.key)) push(`${where}.key`, `구획 키 '${g.key}' 가 중복 선언됐다.`);
      else groupKeys.add(g.key);
      if (typeof g.icon !== 'string' || g.icon.length === 0) {
        push(`${where}.icon`, `구획 아이콘이 없다. 이름의 목록은 packages/ui 의 NAV_ICONS 가 소유하고 ` +
          `\`pnpm web:check\` 가 양방향으로 대조한다.`);
      }
      /* ── 구획 안의 하위 구획(0118) ── */
      if (g.subgroups !== undefined) {
        if (g.subgroups.length <= 1) {
          push(
            `${where}.subgroups`,
            `하위 구획이 ${g.subgroups.length}개다. navGroups 와 같은 규칙이다 — 0개나 1개는 ` +
              `"안 나눈다"와 "나누려다 말았다"가 같은 모양이라 필드를 **쓰지 않는다**.`,
          );
        }
        const subKeys = new Set<string>();
        for (const sg of g.subgroups) {
          const sw = `${where}.subgroups[${sg.key}]`;
          if (!SCREEN_PATH_SEGMENT_RE.test(sg.key)) {
            push(`${sw}.key`, `하위 구획 키 '${sg.key}' 가 소문자 kebab(${SCREEN_PATH_SEGMENT_PATTERN}) 이 아니다. ` +
              `이 값은 내비 키 '<sectionKey>/<key>' 의 뒷자리이고 브라우저 저장소에도 그대로 남는다.`);
          }
          if (subKeys.has(sg.key)) push(`${sw}.key`, `하위 구획 키 '${sg.key}' 가 중복 선언됐다.`);
          else subKeys.add(sg.key);
        }
        subgroupKeys.set(g.key, subKeys);
      }
    }
  }

  for (const s of screens) {
    const where = (field: string) => whereOf(mod.key, s.key, field);

    /* ── 화면 키 ── */
    if (!SCREEN_KEY_RE.test(s.key)) {
      push(where('key'), `화면 키가 <moduleKey>.<rest> 형태가 아니다: '${s.key}'. 소문자 kebab 을 점으로 잇는다.`);
    } else if (s.key.slice(0, s.key.indexOf('.')) !== mod.key) {
      push(
        where('key'),
        `화면 키의 1번 세그먼트가 '${s.key.slice(0, s.key.indexOf('.'))}' 다. 모듈 키 '${mod.key}' 여야 한다 — ` +
          `그래야 모듈 간 화면 키 충돌이 표현 불가능해진다.`,
      );
    }
    if (seenKeys.has(s.key)) push(where('key'), `화면 키 '${s.key}' 가 중복 선언됐다.`);
    else seenKeys.add(s.key);

    /* ── 경로 ── */
    const segments = s.path.split('/');
    if (s.path.length === 0) {
      push(
        where('path'),
        `경로가 비었다. 모듈 루트 화면(/${mod.key}/)을 만들지 않는다 — 로그인 직후 착지는 내비 첫 항목이다(0008 blocking ③).`,
      );
    } else if (s.path.startsWith('/') || s.path.endsWith('/')) {
      push(where('path'), `경로 '${s.path}' 에 앞뒤 슬래시가 있다. 절대경로는 absolutePathOf() 가 만든다.`);
    } else {
      for (const seg of segments) {
        const ok = seg.startsWith(':') ? SCREEN_PATH_PARAM_RE.test(seg) : SCREEN_PATH_SEGMENT_RE.test(seg);
        if (!ok) {
          push(
            where('path'),
            `경로 조각 '${seg}' 이 규약을 벗어난다. 정적 조각은 소문자 kebab(${SCREEN_PATH_SEGMENT_PATTERN}), ` +
              `파라미터는 ':camelCase' 다.`,
          );
        }
      }
    }

    const shape = pathShapeOf(s.path);
    const prevShape = seenShapes.get(shape);
    if (prevShape) {
      push(
        where('path'),
        `경로 '${s.path}' 가 '${prevShape}' 와 같은 라우트다(파라미터 이름은 매칭에 안 쓰인다). ` +
          `둘 중 하나만 매칭되고 다른 하나는 영원히 안 열린다 — 에러도 로그도 없다.`,
      );
    } else {
      seenShapes.set(shape, s.path);
    }

    /* ── 내비에 뜨는 화면은 파라미터를 못 가진다 ── */
    if (!s.hidden && segments.some((seg) => seg.startsWith(':'))) {
      push(
        where('path'),
        `내비에 뜨는 화면인데 경로에 파라미터가 있다: '${s.path}'. 내비에는 그 값을 채울 것이 없어 ` +
          `죽은 링크가 된다(구 시스템의 죽은 링크 6개가 이 형태였다). hidden: true 로 두고 목록 화면에서 링크한다.`,
      );
    }

    /* ── permission ── */
    if (s.kind === 'permission') {
      const requires = s.requires as unknown;
      if (typeof requires !== 'string' || requires.length === 0) {
        push(where('requires'), `kind:'permission' 화면에는 requires 가 필요하다. 권한 없이 열 화면이면 kind:'public' 이다.`);
      } else if (!PERMISSION_KEY_RE.test(requires)) {
        push(
          where('requires'),
          `requires '${requires}' 가 <module>.<domain>.<resource>.<action> 4세그먼트가 아니다. ` +
            `타입은 이걸 못 막는다 — \`\${string}\` 이 점을 포함하므로 5세그먼트도 컴파일된다(실측).`,
        );
      } else {
        const owner = requires.slice(0, requires.indexOf('.'));
        if (owner === mod.key && !declaredPermissions.has(requires)) {
          push(
            where('requires'),
            `requires '${requires}' 가 이 모듈의 RbacManifest 에 없다. ` +
              `다음 행동: 모듈의 RbacManifest 에 permission 을 먼저 선언하고 \`pnpm rbac:sync\` 를 돌린다. ` +
              `이걸 놓치면 서버에서는 403 이지만 웹에서는 "영원히 안 보이는 메뉴"라 더 조용하다.`,
          );
        }
      }
    } else if ((s as { requires?: unknown }).requires !== undefined) {
      push(
        where('requires'),
        `kind:'${s.kind}' 화면에 requires 가 있다. 그 종류는 permission 을 판정하지 않으므로 ` +
          `그 키는 아무도 안 본다 — 붙어 있으면 다음 사람이 게이트가 있다고 믿는다.`,
      );
    }

    /* ── 구획 ── */
    if (!SECTION_VALUES.has(s.section as string)) {
      push(
        where('section'),
        `구획 '${String(s.section)}' 이 목록 밖이다. 코어 구획은 [${NAV_SECTIONS.map((x) => x.key).join(', ')}] ` +
          `이고, vertical 의 화면은 '${OWN_MODULE_SECTION}'(자기 모듈이 곧 구획) 하나만 쓴다. ` +
          `타입이 이미 막지만 캐스트 한 번이나 JS 소비자 하나면 여기로 온다 — 그때 증상은 ` +
          `**내비에서 그 화면만 조용히 사라지는 것**이다.`,
      );
    }

    /* ── 모듈 안의 구획(0099) ── */
    if (s.section !== OWN_MODULE_SECTION) {
      if (s.group !== undefined) {
        push(
          where('group'),
          `코어 구획 '${String(s.section)}' 의 화면에 group 이 붙어 있다. 모듈 안의 구획은 ` +
            `section: '${OWN_MODULE_SECTION}' 일 때만 읽힌다 — 여기 남아 있으면 다음 사람이 ` +
            `그 값이 내비를 바꾼다고 믿는다.`,
        );
      }
    } else if (navGroups === undefined) {
      if (s.group !== undefined) {
        push(
          where('group'),
          `group '${s.group}' 을 골랐는데 이 모듈에 navGroups 선언이 없다. 아무도 안 보는 값이다 — ` +
            `내비에는 모듈 라벨 하나짜리 구획이 그대로 선다. 다음 행동: surface.navGroups 에 구획을 선언한다.`,
        );
      }
    } else if (s.group === undefined) {
      push(
        where('group'),
        `이 모듈은 구획을 [${[...groupKeys].join(', ')}] 로 나눠 선언했는데 이 화면은 어디 사는지 안 적었다. ` +
          `**빠뜨림이 기본값이 되지 않게** 필수다 — 안 적으면 그 화면만 모듈 라벨짜리 구획에 남아 ` +
          `내비에 머리글이 하나 더 생긴다(에러 없이).`,
      );
    } else if (!groupKeys.has(s.group)) {
      push(
        where('group'),
        `group '${s.group}' 이 navGroups 선언에 없다. 선언된 구획은 [${[...groupKeys].join(', ')}] 다 — ` +
          `오타 하나의 증상은 **내비 머리글이 하나 더 생기는 것**이고 그 구획에는 이 화면만 산다.`,
      );
    }

    /* ── 구획 안의 하위 구획(0118) ── */
    const subKeysOfGroup = s.group === undefined ? undefined : subgroupKeys.get(s.group);
    if (subKeysOfGroup === undefined) {
      if (s.subgroup !== undefined) {
        push(
          where('subgroup'),
          `subgroup '${s.subgroup}' 을 골랐는데 이 화면의 구획에 subgroups 선언이 없다. ` +
            `아무도 안 보는 값이다 — 내비에는 구획이 화면을 직접 든 2단이 그대로 선다.`,
        );
      }
    } else if (s.subgroup === undefined) {
      push(
        where('subgroup'),
        `구획 '${s.group!}' 은 하위 구획을 [${[...subKeysOfGroup].join(', ')}] 로 나눠 선언했는데 ` +
          `이 화면은 어디 사는지 안 적었다. **한 구획은 직속 화면과 하위 구획을 동시에 갖지 않는다** — ` +
          `섞이면 머리글 없는 항목이라는 두 번째 모양이 생기고, 그건 0057 이 이미 거절한 형태다.`,
      );
    } else if (!subKeysOfGroup.has(s.subgroup)) {
      push(
        where('subgroup'),
        `subgroup '${s.subgroup}' 이 구획 '${s.group!}' 의 선언에 없다. 선언된 것은 ` +
          `[${[...subKeysOfGroup].join(', ')}] 다 — 오타 하나의 증상은 **그 화면이 내비에서 사라지는 것**이다.`,
      );
    }

    /* ── 아이콘(0118) ── */
    if (typeof s.icon !== 'string' || s.icon.length === 0) {
      push(
        where('icon'),
        `아이콘 이름이 없다. 물음표 없는 필수 필드다 — 선택이면 목록의 절반만 아이콘을 갖게 되고 ` +
          `그 상태는 아이콘이 없는 것보다 나쁘다. 이름의 목록은 packages/ui 의 NAV_ICONS 가 ` +
          `소유하고 \`pnpm web:check\` 가 양방향으로 대조한다.`,
      );
    }

    /* ── 정렬 ── */
    if (s.order !== undefined && !Number.isSafeInteger(s.order)) {
      push(where('order'), `order 가 정수가 아니다: ${String(s.order)}. NaN·소수는 정렬을 조용히 뒤섞는다.`);
    }
  }

  /* ── i18n 키는 i18n.ts 의 판정기가 본다. 규약 사본을 만들지 않는다. ── */
  for (const p of checkI18nKeys(collectSurfaceI18nKeys(mod))) {
    push(p.where, `'${p.key}' — ${p.reason}`);
  }

  return problems;
}

/**
 * 게이트 진입점. `register()`(§구현 순서 8)와 `web:check` 가 부른다.
 * headless 모듈은 화면 0개로 **정상 통과한다** — 여기서 0개는 무력화가 아니라 선언이다.
 * (전 제품의 화면이 0개인 상태는 `resolveSurface` 가 잡는다)
 */
export function assertSurface(mod: {
  key: string;
  rbac: RbacManifest;
  surface: ModuleSurface;
}): { screens: number } {
  const problems = checkSurface(mod);
  if (problems.length) throw new SurfaceError(problems);
  return { screens: screensOf(mod.surface).length };
}

export interface ResolveSurfaceInput {
  /** 조립된 모듈 목록. 순서가 곧 내비 순서다. */
  readonly modules: readonly SurfaceModule[];
  /** 이 직원의 유효 권한 키. `PermissionSet.keys` 를 그대로 넘긴다. */
  readonly granted: ReadonlySet<string>;
  /** 이 테넌트의 활성 모듈. 유일한 생산자는 `packages/db` 의 `enabledModulesOf()` 다. */
  readonly enabledModules: ReadonlySet<string>;
}

/**
 * 매니페스트를 만드는 **순수 함수**. 서버·웹·CI 가 같은 함수를 부른다.
 *
 * '서버가 메뉴를 배달한다'가 두 번째 원본이 되지 않는 유일한 형태다 —
 * 서버는 매니페스트를 발명하지 않고 이 함수의 출력을 실어 보낼 뿐이다.
 * 그리고 클라이언트가 필터하지 않으므로 구 시스템의 **루트-only 권한 필터 버그**
 * (`menuPermissions` 13개 중 자식 경로 4개가 한 번도 조회되지 않았다)가 살 자리가 없다.
 *
 * 던지는 것 둘 / 던지지 않는 것 하나 — **이 구분이 요점이다**:
 *  - 카탈로그가 비었다 → 던진다. 배선 고장이고, 조용히 통과하면 라우트 0개인 앱이 뜬다.
 *  - 플랫폼 모듈이 비활성이다 → 던진다(락아웃).
 *  - **결과가 0개다 → 던지지 않는다.** 권한 0개 신규 직원(`core.member` 는
 *    `assignOnProvision:false`)의 정상 상태다. 코어가 전용 화면을 그린다.
 */
export function resolveSurface(input: ResolveSurfaceInput): SurfaceManifest {
  const catalog = toSurfaceCatalog(input.modules);
  if (catalog.length === 0) {
    throw new SurfaceError([{
      where: '(표면 카탈로그 전체)',
      reason:
        '화면이 하나도 없다. 모든 모듈이 headless 이거나 조립이 깨졌다 — 빈 입력에 초록을 주지 않는다. ' +
        '검사 대상이 0개인 게이트는 통과가 아니라 무력화다.',
    }]);
  }

  // 락아웃 2층: 코드 카탈로그가 "끌 수 없다"고 말하는 모듈이 활성 목록에 있는가.
  const lockedOut = input.modules.filter((m) => !m.licensable && !input.enabledModules.has(m.key));
  if (lockedOut.length > 0) {
    throw new SurfaceLockoutError(
      `플랫폼 모듈 [${lockedOut.map((m) => m.key).join(', ')}] 이 활성 모듈 목록에 없다. ` +
        `licensable=false 는 끌 수 없으므로 이건 데이터가 아니라 고장이다 — ` +
        `테넌트 컨텍스트가 안 걸렸거나(뷰가 0행) 뷰가 카탈로그와 어긋났다. ` +
        `그대로 진행하면 /core/* 전체가 매니페스트에서 사라지고 구독을 되살릴 화면도 못 연다.`,
    );
  }

  const screens: ManifestScreen[] = [];
  for (const e of catalog) {
    // 해지한 모듈의 화면은 매니페스트에 없다. 술어는 `enabledModules` 를 만든 쪽에 있다 —
    // 여기서 `licensable || tenant_modules…` 를 다시 적지 않는다(AGENTS.md 17).
    if (!input.enabledModules.has(e.moduleKey)) continue;

    const s = e.screen;
    /*
     * `public` 과 `authenticated` 는 권한을 보지 않는다. 그래서 착지는 `m.screens[0]` 이
     * 아니라 `deriveNav` 의 출력 위에서 정한다 — 안 그러면 로그인한 사용자가 로그인 화면에
     * 착지한다. (`authenticated` 는 내비에 뜨므로 착지 후보이고, 그건 맞는 동작이다:
     * 권한이 0개인 신규 직원에게 보이는 유일한 화면이 알림함이다)
     */
    if (s.kind === 'permission' && !input.granted.has(s.requires)) continue;

    screens.push({
      key: s.key,
      path: e.path,
      moduleKey: e.moduleKey,
      moduleLabelKey: e.moduleLabelKey,
      moduleIcon: e.moduleIcon,
      section: s.section,
      group: e.group,
      subgroup: e.subgroup,
      labelKey: s.labelKey,
      titleKey: s.titleKey ?? s.labelKey,
      kind: s.kind,
      hidden: s.hidden ?? false,
      order: s.order ?? 0,
      icon: s.icon,
    });
  }
  return { screens };
}

/**
 * 내비 — 2단(**구획** → 화면), 아이콘 없음.
 *
 * ─── 1단이 모듈이 아니라 구획이다 (2026-09-12, 0057) ───
 * 그 이유는 `NAV_SECTIONS` 에 있다. 여기서 달라진 것은 **묶는 키 하나**이고,
 * 2단이라는 것도 아이콘이 없다는 것도 그대로다.
 *
 * 아이콘을 안 넣은 이유: 닫힌 아이콘 이름 목록은 **네 번째 손 관리 목록**이 되고
 * (`NAV_SECTIONS`·`RESERVED_MODULE_KEYS`·`PRODUCT_MODULES` 에 이어), vertical 개념을
 * 일반명사로 코어에 들인다 — 제안된 목록에 이미 `camera` 가 있었고 순수성 검사는 그걸 못 잡는다.
 * 구 시스템의 3단 + 아이콘 46개는 그 값을 증명한 적이 없다.
 *
 * ─── 빈 구획은 없다 ───
 * 구획은 **화면에서 파생한다**(`NAV_SECTIONS` 를 순회하지 않는다). 모듈을 해지했거나
 * 권한이 없어 그 구획의 화면이 0개면 구획 자체가 안 나온다 — 머리글만 남은 빈 구획은
 * 사용자에게 "여기 뭔가 있는데 안 보인다"이고, 그건 없는 것보다 나쁘다.
 *
 * **재검토 트리거를 미리 적는다: 한 구획의 화면이 12개를 넘는 첫 PR.**
 * (지금 가장 큰 구획이 10개다. 12 는 접힌 구획 하나가 한 화면 안에서 스크롤 없이
 * 보이는 마지막 자리다 — 그 위로는 3단이 아니라 **구획을 쪼개는 것**이 답이다)
 *
 * ★ **그 트리거가 발동했다 (2026-09-15, 0099).** `stationon` 이 23개였다 — 선을 두 배
 *   넘겼고, 넘친 것이 길이만이 아니었다: 한 머리글 아래 창고 작업·쿠팡 판매·원단 마스터
 *   셋이 섞여 있었다. 답은 위에 적힌 그대로 **구획을 쪼개는 것**이었고, 3단은 아니다
 *   (`navGroups` — 내비는 여전히 2단이고 구획의 수만 늘었다).
 */
/**
 * 이 화면이 사는 **내비 구획의 키**. 조립하는 자리를 하나로 묶는다.
 *
 * `deriveNav` · `navSectionOf` · `apps/server` 의 order 충돌 게이트가 같은 함수를 부른다 —
 * 셋이 각자 `${moduleKey}.${group.key}` 를 적으면 갈라질 수 있고, **갈라진 날의 증상은
 * 빵부스러기가 내비에 없는 구획을 가리키는 것**이다(에러 없이).
 *
 * 입력이 `ManifestScreen` 이 아니라 구조적 부분집합인 이유: 게이트는 매니페스트가
 * 아니라 카탈로그를 순회한다(볼 수 없는 화면까지 세야 하므로).
 */
export function navSectionKeyOf(s: {
  readonly section: ScreenSection;
  readonly moduleKey: string;
  readonly group: { readonly key: string } | null;
}): string {
  if (s.section !== OWN_MODULE_SECTION) return s.section;
  return s.group === null ? s.moduleKey : `${s.moduleKey}.${s.group.key}`;
}

export function deriveNav(manifest: SurfaceManifest): NavSection[] {
  const order: string[] = [];
  const bySection = new Map<
    string,
    {
      labelKey: string; icon: string; screens: ManifestScreen[];
      /** 하위 구획. 키는 선언의 `subgroup.key`, 순서는 `subgroup.order` 다. */
      subgroups: Map<string, { labelKey: string; order: number; screens: ManifestScreen[] }>;
      moduleRank: number; groupRank: number;
    }
  >();

  /*
   * 코어 구획을 **화면보다 먼저** 자리잡게 한다. 이게 없으면 구획 순서가
   * "그 구획의 첫 화면이 어느 모듈에서 왔나"로 정해진다 — `NAV_SECTIONS` 에 적힌
   * 순서가 아니라 모듈 등록 순서가 이기고, 그 둘이 어긋나도 아무 신호가 없다.
   */
  const sectionIndex = new Map<string, number>(NAV_SECTIONS.map((s, i) => [s.key, i]));

  /** 모듈이 처음 나타난 순서 = 모듈 등록 순서(카탈로그가 모듈 단위로 평평해진다). */
  const moduleFirstSeen = new Map<string, number>();

  for (const s of manifest.screens) {
    if (s.hidden || s.kind === 'public') continue;
    if (!moduleFirstSeen.has(s.moduleKey)) moduleFirstSeen.set(s.moduleKey, moduleFirstSeen.size);
    /*
     * vertical 은 자기 모듈이 구획이다. 코어 구획 키와 겹칠 수 없다(RESERVED_MODULE_KEYS).
     * 구획을 여럿 선언한 모듈은 `<moduleKey>.<groupKey>` 로 갈라진다(0099) — 점이 들어가므로
     * 모듈 키 하나짜리 구획과도, 코어 구획과도 이름이 겹칠 수 없다.
     */
    const key = navSectionKeyOf(s);
    let bucket = bySection.get(key);
    if (!bucket) {
      const core = NAV_SECTIONS.find((x) => x.key === key);
      bucket = {
        labelKey: core?.labelKey ?? s.group?.labelKey ?? s.moduleLabelKey,
        /*
         * 1단 아이콘의 출처가 셋이다 — 코어 구획 · 모듈 구획 · 구획 없는 모듈.
         * 마지막은 `ModuleSurface.navIcon` 이 화면마다 실려 온다(`moduleIcon`):
         * 모듈 라벨을 화면마다 들고 있는 것과 같은 판단이다.
         */
        icon: core?.icon ?? s.group?.icon ?? s.moduleIcon,
        screens: [],
        subgroups: new Map(),
        moduleRank: moduleFirstSeen.get(s.moduleKey)!,
        groupRank: s.group?.order ?? 0,
      };
      bySection.set(key, bucket);
      order.push(key);
    }
    if (s.subgroup === null) {
      bucket.screens.push(s);
    } else {
      let sub = bucket.subgroups.get(s.subgroup.key);
      if (!sub) {
        sub = { labelKey: s.subgroup.labelKey, order: s.subgroup.order, screens: [] };
        bucket.subgroups.set(s.subgroup.key, sub);
      }
      sub.screens.push(s);
    }
  }

  /*
   * 코어 구획은 선언 순서로, vertical 구획은 그 **뒤에** 모듈 등록 순서로.
   * 한 모듈이 구획 여럿이면 그 안에서는 `navGroups` 선언 순서다 — 화면 선언 순서가
   * 아니다(0099). `sort` 는 stable 하므로 셋이 다 같으면 위 `order` 의 순서가 남는다.
   */
  const rank = (key: string) => sectionIndex.get(key) ?? NAV_SECTIONS.length;

  return [...order]
    .sort((a, b) => {
      const byRank = rank(a) - rank(b);
      if (byRank !== 0) return byRank;
      const ga = bySection.get(a)!;
      const gb = bySection.get(b)!;
      return ga.moduleRank - gb.moduleRank || ga.groupRank - gb.groupRank;
    })
    .map((key) => {
      const g = bySection.get(key)!;
      // Array.prototype.sort 는 stable 하다 — order 가 같으면 선언 순서가 남는다.
      const byOrder = (a: ManifestScreen, b: ManifestScreen) => a.order - b.order;
      return {
        key,
        labelKey: g.labelKey,
        icon: g.icon,
        screens: [...g.screens].sort(byOrder),
        /*
         * 하위 구획의 순서는 **선언 순서**(`subgroups` 배열)이지 화면 order 가 아니다.
         * 화면으로 정하면 "그 구획의 첫 화면이 몇 번인가"가 순서가 되고, 그건 위에서
         * 코어 구획에 대해 이미 거절한 형태다(`sectionIndex` 선점이 그 거절이다).
         * 키는 `<sectionKey>/<subgroupKey>` — **전역 유일**이라 접힘 상태가 배열 하나다.
         */
        groups: [...g.subgroups.entries()]
          .sort(([, a], [, b]) => a.order - b.order)
          .map(([subKey, sub]) => ({
            key: `${key}/${subKey}`,
            labelKey: sub.labelKey,
            screens: [...sub.screens].sort(byOrder),
          })),
      };
    });
}

/**
 * 내비에 뜨는 화면 키 전부 — **평평하게.** (0118)
 *
 * ─── 왜 함수인가 ───
 * 3단이 되기 전에는 `nav.flatMap((g) => g.screens.map((s) => s.key))` 한 줄이었고 그것이
 * **두 곳에 적혀 있었다**(`web:check` 와 그 시험). 하위 구획이 생기자 그 한 줄이 조용히
 * 틀렸다 — 구획 직속 화면만 세고 하위 구획 안의 것을 빠뜨린다. 증상이 에러가 아니라
 * **게이트의 대상이 71에서 57로 줄어드는 것**이라, 초록인 채로 열넷이 감사 밖으로 나간다.
 *
 * 그게 이 리포가 반복해서 값을 치른 형태다(0002 경계 게이트가 해석 실패로 무력화,
 * 0007 exclude 삭제). 그래서 평평하게 만드는 자리를 하나로 둔다.
 */
export function navScreenKeysOf(nav: readonly NavSection[]): string[] {
  return nav.flatMap((sec) => [
    ...sec.screens.map((s) => s.key),
    ...sec.groups.flatMap((g) => g.screens.map((s) => s.key)),
  ]);
}

/** 화면이 속한 구획. 없으면 undefined — 매니페스트에 없는 화면이다. */
export function navSectionOf(
  manifest: SurfaceManifest,
  screenKey: string,
): { readonly key: string; readonly labelKey: string } | undefined {
  const s = screenOf(manifest, screenKey);
  if (!s) return undefined;
  if (s.section === OWN_MODULE_SECTION) {
    // 키 조립의 소유자는 `navSectionKeyOf` 하나다 — 여기 다시 적으면 빵부스러기가
    // 내비에 없는 구획을 가리킬 수 있고, 그 상태에 에러가 없다.
    return { key: navSectionKeyOf(s), labelKey: s.group?.labelKey ?? s.moduleLabelKey };
  }
  const core = NAV_SECTIONS.find((x) => x.key === s.section);
  // 목록 밖의 구획은 타입이 막는다. 그래도 여기서 던지지 않는 이유는 빵부스러기와 같다.
  return core ? { key: core.key, labelKey: core.labelKey } : undefined;
}

/**
 * 로그인 직후·로고 클릭·`/` 의 착지.
 *
 * **내비 첫 항목이다. 두 번째 손잡이를 만들지 않는다** — `landing?: screenKey` 필드는
 * 메뉴 첫 항목과 착지가 어긋날 수 있는 상태를 새로 만든다. 구 `bottomNavDefaultPaths`(17행)가
 * 정확히 그 필드였고 트리와 이미 어긋나 있었다.
 * `undefined` = 보이는 화면이 0개인 사용자. 빈 화면도 거부 화면도 아닌 **전용 화면**을 코어가 그린다.
 */
export function landingOf(nav: readonly NavSection[]): ManifestScreen | undefined {
  const first = nav[0];
  /* 3단 구획은 직속 화면이 없다(`screens` 와 `groups` 는 동시에 차지 않는다). */
  return first?.screens[0] ?? first?.groups[0]?.screens[0];
}

/** 매니페스트에서 화면 하나. 없으면 undefined — 매니페스트에 없는 화면은 이 사용자에게 없는 화면이다. */
export function screenOf(manifest: SurfaceManifest, screenKey: string): ManifestScreen | undefined {
  return manifest.screens.find((s) => s.key === screenKey);
}

/**
 * 빵부스러기 — [구획, (하위 구획), 화면]. 깊이의 상한이 3이므로 재귀가 없다.
 *
 * 경로 문자열의 `startsWith` 로 조상을 찾지 않는다. 그 방식은 루트 표현이 `'/'` 냐 `''` 냐로
 * **조용히 갈린다**(`'/pay/runs'.startsWith('//')` = false vs `startsWith('/')` = true).
 * 여기서는 매칭된 라우트의 screenKey 로 조회할 뿐이다.
 *
 * 모르는 키에 던지지 않는다: 빵부스러기 하나 때문에 화면 전체가 죽는 건 비례가 안 맞는다.
 * 선언 불일치는 CI 에서 `web:check`(G7 라우트·내비 일치)가 잡는다.
 */
export function deriveBreadcrumbs(manifest: SurfaceManifest, screenKey: string): Breadcrumb[] {
  const s = screenOf(manifest, screenKey);
  if (!s) return [];
  /*
   * 1단은 **구획**이다(2026-09-12, 0057). 모듈 라벨이 아닌 이유: 여기가 말하는 것은
   * "지금 어디에 있나"이고 그 답은 사용자가 방금 누른 자리 — 즉 내비의 1단이어야 한다.
   * 모듈 라벨을 쓰면 급여 계약 화면의 위치가 내비에서는 '급여·정산'인데 머리줄에서는
   * '급여'가 되어, 같은 화면이 두 이름으로 불린다.
   */
  const section = navSectionOf(manifest, screenKey);
  /*
   * 하위 구획이 있으면 **가운데에 낀다**(0118) — 내비가 세 층이면 "지금 어디에 있나"도
   * 세 층이다. 없으면 예전 그대로 둘이다: 깊이를 상수로 고정하지 않는 이유는 그 상수가
   * 곧 "3단이 없다"의 두 번째 사본이 되기 때문이다.
   */
  return [
    { labelKey: section?.labelKey ?? s.moduleLabelKey },
    ...(s.subgroup === null ? [] : [{ labelKey: s.subgroup.labelKey }]),
    { labelKey: s.labelKey, path: s.path },
  ];
}

/**
 * 문서 제목의 i18n 키. 구 시스템의 `pageTitleKeyMap`(33행, 라우트와 이미 어긋나 있었다)이
 * 이 함수 하나로 사라진다.
 */
export function documentTitleKeyOf(manifest: SurfaceManifest, screenKey: string): string | undefined {
  return screenOf(manifest, screenKey)?.titleKey;
}

/**
 * permission 키 → 그 키가 여는 화면들. 역할 편집 화면이 "이 권한을 주면 이 화면들이 열린다"를
 * 표시하는 데 쓴다.
 *
 * 입력이 매니페스트가 아니라 **전체 카탈로그**인 이유: 관리자는 자기가 못 보는 화면도
 * 배정해야 한다. granted-only 매니페스트에서 파생하면 그 표는 관리자 자신의 권한만큼만 보인다
 * (구 `RolesView.vue` 896줄이 손으로 적혀 있던 이유가 이 파생 불가능성이었다).
 * 이 표를 주는 procedure 는 `core.rbac.role.read` 로 게이트한다.
 */
export function screensByPermission(
  catalog: readonly SurfaceCatalogEntry[],
): Map<string, SurfaceCatalogEntry[]> {
  const out = new Map<string, SurfaceCatalogEntry[]>();
  for (const e of catalog) {
    if (e.screen.kind !== 'permission') continue;
    const list = out.get(e.screen.requires);
    if (list) list.push(e);
    else out.set(e.screen.requires, [e]);
  }
  return out;
}

/**
 * 표면 지문의 **정본 직렬화**. `surfaceHash` 의 입력이 되는 문자열이다.
 *
 * ─── 무엇을 넣는가 = 무엇을 배포 스큐로 볼 것인가 ───
 * 클라이언트가 캐시한 매니페스트·라우트 표가 의존하는 필드 전부다. 기본값(`titleKey`·
 * `hidden`·`order`)은 **해소한 뒤** 넣는다 — `titleKey` 를 생략에서 명시로 바꾸기만 한 배포는
 * 클라이언트가 보는 것이 한 글자도 안 바뀌므로 스큐가 아니다. 반대로 `order` 한 칸 변경은
 * 내비 순서가 바뀌고 **착지 화면까지 바뀌므로**(landingOf = 내비 첫 항목) 스큐다.
 *
 * ─── 정렬하지 않는다 ───
 * `evalHashOf` 는 키 목록을 정렬한다. 거긴 집합이기 때문이다. 여기서 순서는 **의미**다 —
 * 모듈 순서가 내비 순서이고 화면 순서가 같은 `order` 안의 tiebreak 다. 정렬하면
 * "메뉴 순서만 바꾼 배포"가 같은 지문을 내고, 그게 정확히 스큐로 잡아야 하는 변경이다.
 *
 * 필드 구분자로 탭·개행을 쓸 수 있는 근거: 모든 값이 위 정규식들로 제한된 식별자이고
 * (`SCREEN_KEY_RE`·`SCREEN_PATH_SEGMENT_RE`·i18n `REST_RE`) 공백을 포함할 수 없다.
 */
export const SURFACE_FINGERPRINT_VERSION = 4;

export function surfaceFingerprint(catalog: readonly SurfaceCatalogEntry[]): string {
  const rows = catalog.map((e) => {
    const s = e.screen;
    return [
      e.moduleKey,
      e.moduleLabelKey,
      s.key,
      e.path,
      // 구획은 내비 순서와 착지를 바꾼다(landingOf = 내비 첫 항목) — 스큐다.
      s.section,
      /*
       * 모듈 안의 구획(0099). 키만이 아니라 **이름과 순서까지** 넣는다 — 셋 다 클라이언트가
       * 그리는 것이고, 특히 순서는 첫 구획의 첫 화면(= 착지)을 바꾼다.
       */
      e.group === null ? '' : `${e.group.key}:${e.group.labelKey}:${e.group.order}:${e.group.icon}`,
      /* 하위 구획(0118). 구획과 같은 이유로 키·이름·순서 셋 다 넣는다. */
      e.subgroup === null ? '' : `${e.subgroup.key}:${e.subgroup.labelKey}:${e.subgroup.order}`,
      s.labelKey,
      s.titleKey ?? s.labelKey,
      s.kind,
      s.hidden ?? false,
      s.order ?? 0,
      // kind:'public' 은 requires 가 없다. 빈 칸이 곧 "권한을 안 본다"이고,
      // permission → public 으로 바뀐 화면은 이 칸이 비면서 지문이 달라진다.
      s.kind === 'permission' ? s.requires : '',
      /* 아이콘(0118). 클라이언트가 그리는 것이고, 바뀌면 화면이 달라 보인다 — 스큐다. */
      s.icon,
    ].join('\t');
  });
  return [`surface.v${SURFACE_FINGERPRINT_VERSION}`, ...rows].join('\n');
}

/**
 * 경로 하나를 **전체 카탈로그**에서 되찾는다. `session.describePath` 의 판정기다.
 *
 * ─── 왜 매니페스트가 아니라 카탈로그인가 ───
 * 매니페스트에는 이 사용자가 **볼 수 있는** 화면만 있다. 거부를 설명하려면 볼 수 없는
 * 화면까지 아는 목록이 필요하고, 그게 카탈로그다. 그래서 이 함수를 부르는 자리는
 * 서버 하나뿐이다 — 브라우저에 카탈로그를 내려보내면 그것이 곧 화면 지도 유출이다.
 *
 * ─── 파라미터 ───
 * 선언 경로는 `:runId` 같은 조각을 가질 수 있다(`SCREEN_PATH_PARAM_RE`). 그 조각은
 * 아무 한 조각과 맞는다. 정규식을 만들어 쓰지 않는 이유는 이스케이프 사고 때문이다 —
 * 조각 비교는 그런 사고가 없고, 경로 깊이가 한 자리 수라 성능 문제도 아니다.
 *
 * ─── 첫 일치를 돌려준다 ───
 * 경로 충돌은 `checkSurface` 가 선언 시점에 막으므로 둘 이상이 맞는 상태는 조립이 깨진
 * 경우뿐이다. 그때 여기서 다시 판정하면 **두 번째 판정기**가 생긴다.
 */
export function matchScreenPath(
  catalog: readonly SurfaceCatalogEntry[],
  rawPath: string,
): SurfaceCatalogEntry | undefined {
  const wanted = splitPath(rawPath);
  if (wanted.length === 0) return undefined;
  return catalog.find((e) => {
    const declared = splitPath(e.path);
    if (declared.length !== wanted.length) return false;
    return declared.every((seg, i) => (seg.startsWith(':') ? true : seg === wanted[i]));
  });
}

/**
 * 쿼리·해시를 버리고 조각으로 나눈다. 후행 슬래시도 여기서 사라진다 —
 * `/core/employees` 와 `/core/employees/` 가 다른 화면일 수 있는 상태를 만들지 않는다.
 */
function splitPath(path: string): string[] {
  const clean = path.split('?')[0]!.split('#')[0]!;
  return clean.split('/').filter((s) => s.length > 0);
}
